/**
 * Trash — the recoverable Trash view (2026-05-22, owner decision).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  Deleted leads / clients / projects / partners are not hard-deleted — they
 *  go to a recoverable Trash (`trashed:true`). This screen lists every
 *  trashed record, in four tabs, each row with:
 *    · Restore           — clears the flag; the record returns to its
 *                          normal place, fully intact.
 *    · Delete permanently — the irreversible hard delete, via the admin-
 *                          only `purgeTrashedRecord` Cloud Function.
 *  Plus bulk multi-select (Restore selected · Delete selected) and an
 *  "Empty Trash" action. All four collections — including partners — are
 *  first-class: per-record and bulk permanent delete cover every tab
 *  (owner decision 2026-05-22).
 *
 *  Capability-gated: the route is admin-gated (App.tsx) and the screen also
 *  checks `canManageTrash` / `canPurgeTrash`. Staff park/reject is a
 *  separate, unchanged flow — staff never reach this screen.
 *
 *  Restore writes (clear the `trashed` flag) are ordinary client-SDK writes
 *  — `firestore.rules` already permit them. Permanent purge is the Cloud
 *  Function (admin SDK bypasses `allow delete: if false`).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Box, Btn3, Card, EmptyNote, Pill, Tag } from '../components/ui';
import {
  listenTrashedProjects,
  listenTrashedClients,
  listenTrashedPartners,
  trashProject,
  trashClient,
  trashPartner,
  type LiveProject,
  type LiveClient,
  type LivePartner,
} from '../lib/projectStore';
import {
  listenTrashedLeads,
  restoreLeadDoc,
} from '../lib/leadStore';
import { LEAD_SOURCE_LABEL, LEAD_STATE_LABEL, type Lead } from '../lib/leads';
import {
  listenPropertyTrash,
  trashProperty,
  restorePropertyChild,
  purgePropertyChild,
} from '../lib/propertyStore';
import type { DeletedPropertyChild, LiveProperty } from '../lib/propertyModel';
import {
  listenTrashedFamilies,
  restoreFamily,
  type FamilyGroup,
} from '../lib/familyStore';
import {
  canManageTrash,
  canPurgeTrash,
  emptyTrash,
  purgeTrashItem,
  type TrashKind,
} from '../lib/trash';

const nowISO = () => new Date().toISOString();

type Tab = 'leads' | 'clients' | 'projects' | 'partners' | 'properties' | 'groups';

export default function Trash() {
  const { role, user } = useAuth();
  const { toast } = useToast();
  const actorName = user?.displayName || user?.email?.split('@')[0] || 'You';
  const mayManage = canManageTrash(role);
  const mayPurge = canPurgeTrash(role);

  const [tab, setTab] = useState<Tab>('leads');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [clients, setClients] = useState<LiveClient[]>([]);
  const [projects, setProjects] = useState<LiveProject[]>([]);
  // Partners ride the same Trash model as leads / clients / projects — a
  // trashed partner is restorable AND (owner decision 2026-05-22)
  // permanently purgeable through the same admin-only Cloud Function.
  const [partners, setPartners] = useState<LivePartner[]>([]);
  // AD-038 — whole-property soft-deletes + soft-deleted nested concepts/versions/
  // floors (the latter aren't separate docs; restore clears the flag, purge is a
  // client-SDK array update).
  const [trashProps, setTrashProps] = useState<LiveProperty[]>([]);
  const [trashChildren, setTrashChildren] = useState<DeletedPropertyChild[]>([]);
  // Groups (families / organisations) — recoverable soft-delete (Silas's
  // trashFamily/restoreFamily). Restore-only in Trash for now (no purge CF).
  const [groups, setGroups] = useState<FamilyGroup[]>([]);
  // Multi-select — a Set of doc ids, scoped to the active tab.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const unsub = listenTrashedLeads(setLeads, () =>
      toast('Could not load trashed leads'),
    );
    return unsub;
  }, [toast]);
  useEffect(() => {
    const unsub = listenTrashedClients(setClients, () =>
      toast('Could not load trashed clients'),
    );
    return unsub;
  }, [toast]);
  useEffect(() => {
    const unsub = listenTrashedProjects(setProjects, () =>
      toast('Could not load trashed projects'),
    );
    return unsub;
  }, [toast]);
  useEffect(() => {
    const unsub = listenTrashedPartners(setPartners, () =>
      toast('Could not load trashed partners'),
    );
    return unsub;
  }, [toast]);
  useEffect(() => {
    const unsub = listenPropertyTrash(({ properties, children }) => {
      setTrashProps(properties);
      setTrashChildren(children);
    });
    return unsub;
  }, []);
  useEffect(() => {
    const unsub = listenTrashedFamilies(setGroups, {
      onErr: () => toast('Could not load trashed groups'),
    });
    return unsub;
  }, [toast]);

  // Switching tab clears the selection (ids do not cross collections).
  useEffect(() => setSelected(new Set()), [tab]);

  const propTrashCount = trashProps.length + trashChildren.length;
  const counts = {
    leads: leads.length,
    clients: clients.length,
    projects: projects.length,
    partners: partners.length,
    properties: propTrashCount,
    groups: groups.length,
  };
  const totalTrashed =
    counts.leads + counts.clients + counts.projects + counts.partners;

  /** The id list for the active tab — drives select-all. The properties tab uses
   *  per-row actions only (whole-property + nested children differ), so it does
   *  not participate in the multi-select bulk bar. */
  const tabIds = useMemo<string[]>(() => {
    if (tab === 'leads') return leads.map((l) => l.id);
    if (tab === 'clients') return clients.map((c) => c.cid);
    if (tab === 'projects') return projects.map((p) => p.pid);
    if (tab === 'partners') return partners.map((p) => p.pid);
    return [];
  }, [tab, leads, clients, projects, partners]);

  function toggle(id: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((s) =>
      s.size === tabIds.length ? new Set() : new Set(tabIds),
    );
  }

  /* ── Restore (clear the trashed flag — ordinary client-SDK write) ── */
  async function restoreOne(kind: 'lead' | 'client' | 'project' | 'partner', id: string) {
    if (busy) return;
    setBusy(true);
    try {
      if (kind === 'lead') {
        const lead = leads.find((l) => l.id === id);
        if (lead) await restoreLeadDoc(lead, actorName, nowISO());
      } else if (kind === 'client') {
        await trashClient(id, actorName, false);
      } else if (kind === 'project') {
        await trashProject(id, actorName, false);
      } else {
        await trashPartner(id, actorName, false);
      }
      toast('Restored — the record is back in its normal place.');
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    } catch (e) {
      toast(`Restore failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /* ── Permanent purge (admin-only Cloud Function) ── */
  async function purgeOne(kind: 'lead' | 'client' | 'project' | 'partner', id: string) {
    if (busy || !mayPurge) return;
    if (
      !window.confirm(
        'Permanently delete this record? This CANNOT be undone.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await purgeTrashItem(kind, id);
      toast('Permanently deleted.');
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    } catch (e) {
      toast(`Delete failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /* ── Bulk restore ── */
  async function restoreSelected() {
    if (busy || selected.size === 0) return;
    setBusy(true);
    let ok = 0;
    let fail = 0;
    for (const id of selected) {
      try {
        if (tab === 'leads') {
          const lead = leads.find((l) => l.id === id);
          if (lead) await restoreLeadDoc(lead, actorName, nowISO());
        } else if (tab === 'clients') {
          await trashClient(id, actorName, false);
        } else if (tab === 'projects') {
          await trashProject(id, actorName, false);
        } else {
          await trashPartner(id, actorName, false);
        }
        ok++;
      } catch {
        fail++;
      }
    }
    setBusy(false);
    setSelected(new Set());
    toast(
      `Restored ${ok} record${ok === 1 ? '' : 's'}` +
        (fail ? ` · ${fail} failed` : '') +
        '.',
    );
  }

  /* ── Bulk permanent delete ── */
  async function purgeSelected() {
    if (busy || selected.size === 0 || !mayPurge) return;
    if (
      !window.confirm(
        `Permanently delete ${selected.size} record` +
          `${selected.size === 1 ? '' : 's'}? This CANNOT be undone.`,
      )
    ) {
      return;
    }
    setBusy(true);
    let ok = 0;
    let fail = 0;
    // Every tab — leads / clients / projects / partners — maps to a
    // TrashKind the purge CF accepts.
    const kind: TrashKind =
      tab === 'leads'
        ? 'lead'
        : tab === 'clients'
          ? 'client'
          : tab === 'projects'
            ? 'project'
            : 'partner';
    for (const id of selected) {
      try {
        await purgeTrashItem(kind, id);
        ok++;
      } catch {
        fail++;
      }
    }
    setBusy(false);
    setSelected(new Set());
    toast(
      `Permanently deleted ${ok} record${ok === 1 ? '' : 's'}` +
        (fail ? ` · ${fail} failed` : '') +
        '.',
    );
  }

  /* ── Empty Trash (every trashed lead / client / project / partner) ── */
  async function doEmptyTrash() {
    if (busy || !mayPurge) return;
    if (
      !window.confirm(
        'Permanently delete EVERY trashed lead, client, project and ' +
          'partner? This CANNOT be undone.',
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await emptyTrash();
      toast(
        `Emptied Trash — ${res.totalDeleted} record` +
          `${res.totalDeleted === 1 ? '' : 's'} permanently deleted` +
          (res.more ? ' (run again to finish the rest)' : '') +
          '.',
      );
    } catch (e) {
      toast(`Empty Trash failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /* ── AD-038 — restore a whole soft-deleted property (clears the flag) ── */
  async function restoreProperty(propertyId: string) {
    if (busy) return;
    setBusy(true);
    try {
      await trashProperty(propertyId, actorName, false);
      toast('Property restored — back in its normal place.');
    } catch (e) {
      toast(`Restore failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /* ── AD-038 — restore a soft-deleted nested concept/version/floor ── */
  async function restoreChild(child: DeletedPropertyChild) {
    if (busy) return;
    setBusy(true);
    try {
      await restorePropertyChild(child);
      toast('Restored — back in its property.');
    } catch (e) {
      toast(`Restore failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /* ── Restore a soft-deleted Group (clears the trashed flag) ── */
  async function restoreGroup(id: string) {
    if (busy) return;
    setBusy(true);
    try {
      await restoreFamily(id, actorName);
      toast('Group restored — back in Manage → Groups.');
    } catch (e) {
      toast(`Restore failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  /* ── AD-038 — permanently purge a nested item (client-SDK array update) ── */
  async function purgeChild(child: DeletedPropertyChild) {
    if (busy || !mayPurge) return;
    if (!window.confirm('Permanently delete this item? This CANNOT be undone.')) return;
    setBusy(true);
    try {
      await purgePropertyChild(child);
      toast('Permanently deleted.');
    } catch (e) {
      toast(`Delete failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!mayManage) {
    return (
      <Box title="Trash" note="Recoverable Trash">
        <EmptyNote title="Not available">
          The Trash is an admin-only screen.
        </EmptyNote>
      </Box>
    );
  }

  const allSelected = tabIds.length > 0 && selected.size === tabIds.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Box
        title="Trash"
        note={`${totalTrashed} record${totalTrashed === 1 ? '' : 's'} in Trash — restore, or permanently delete`}
      >
        <p
          style={{
            fontSize: 12.5,
            color: 'var(--ink-mute)',
            margin: '0 0 12px',
            lineHeight: 1.6,
          }}
        >
          A trashed record is hidden from every normal list, search and
          count — but it is not deleted. Restore brings it back intact.
          Permanent deletion is irreversible and admin-only.
        </p>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Pill on={tab === 'leads'} onClick={() => setTab('leads')}>
            Leads ({counts.leads})
          </Pill>
          <Pill on={tab === 'clients'} onClick={() => setTab('clients')}>
            Clients ({counts.clients})
          </Pill>
          <Pill on={tab === 'projects'} onClick={() => setTab('projects')}>
            Projects ({counts.projects})
          </Pill>
          <Pill on={tab === 'partners'} onClick={() => setTab('partners')}>
            Partners ({counts.partners})
          </Pill>
          <Pill on={tab === 'properties'} onClick={() => setTab('properties')}>
            Properties ({counts.properties})
          </Pill>
          <Pill on={tab === 'groups'} onClick={() => setTab('groups')}>
            Groups ({counts.groups})
          </Pill>
        </div>

        {/* Bulk-action bar — not shown on the properties or groups tabs
            (per-row restore only; Empty Trash / bulk purge never touch them —
            neither is wired through the purge CF). */}
        {tab !== 'properties' && tab !== 'groups' && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: 12,
          }}
        >
          {tabIds.length > 0 && (
            <label
              style={{
                fontSize: 12.5,
                color: 'var(--ink-soft)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <input
                type="checkbox"
                checked={allSelected}
                onChange={toggleAll}
              />
              Select all ({tabIds.length})
            </label>
          )}
          {selected.size > 0 && (
            <>
              <span style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
                {selected.size} selected
              </span>
              <Btn3
                intent="grn"
                size="sm"
                onClick={restoreSelected}
                disabled={busy}
              >
                Restore selected
              </Btn3>
              {mayPurge && (
                <Btn3
                  intent="ghost"
                  size="sm"
                  onClick={purgeSelected}
                  disabled={busy}
                >
                  Delete selected permanently
                </Btn3>
              )}
            </>
          )}
          {mayPurge && (
            <Btn3
              intent="ghost"
              size="sm"
              onClick={doEmptyTrash}
              disabled={busy || totalTrashed === 0}
              className=""
            >
              Empty Trash
            </Btn3>
          )}
        </div>
        )}
      </Box>

      {/* AD-038 — Properties tab: whole-property soft-deletes + nested
          concept/version/floor soft-deletes. */}
      {tab === 'properties' ? (
        <PropertyTrashSection
          properties={trashProps}
          children={trashChildren}
          busy={busy}
          mayPurge={mayPurge}
          onRestoreProperty={restoreProperty}
          onRestoreChild={restoreChild}
          onPurgeChild={purgeChild}
        />
      ) : tab === 'groups' ? (
        <GroupTrashSection groups={groups} busy={busy} onRestore={restoreGroup} />
      ) : (
      /* Rows for the active tab */
      <Box
        title={
          tab === 'leads'
            ? 'Trashed leads'
            : tab === 'clients'
              ? 'Trashed clients'
              : tab === 'projects'
                ? 'Trashed projects'
                : 'Trashed partners'
        }
        note={`${tabIds.length} record${tabIds.length === 1 ? '' : 's'}`}
      >
        {tabIds.length === 0 ? (
          <EmptyNote title="Nothing here">
            No {tab} in the Trash.
          </EmptyNote>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tab === 'leads' &&
              leads.map((l) => (
                <TrashRow
                  key={l.id}
                  id={l.id}
                  selected={selected.has(l.id)}
                  onToggle={() => toggle(l.id)}
                  title={l.name}
                  subtitle={`${l.id} · ${LEAD_SOURCE_LABEL[l.source]} · ${LEAD_STATE_LABEL[l.state]}`}
                  trashedBy={l.trashedBy ?? null}
                  trashedAt={l.trashedAt ?? null}
                  busy={busy}
                  canPurge={mayPurge}
                  onRestore={() => restoreOne('lead', l.id)}
                  onPurge={() => purgeOne('lead', l.id)}
                />
              ))}
            {tab === 'clients' &&
              clients.map((c) => (
                <TrashRow
                  key={c.cid}
                  id={c.cid}
                  selected={selected.has(c.cid)}
                  onToggle={() => toggle(c.cid)}
                  title={c.name || c.clientNo}
                  subtitle={`${c.clientNo}${c.phone ? ` · ${c.phone}` : ''}`}
                  trashedBy={c.trashedBy}
                  trashedAt={c.trashedAt}
                  busy={busy}
                  canPurge={mayPurge}
                  onRestore={() => restoreOne('client', c.cid)}
                  onPurge={() => purgeOne('client', c.cid)}
                />
              ))}
            {tab === 'projects' &&
              projects.map((p) => (
                <TrashRow
                  key={p.pid}
                  id={p.pid}
                  selected={selected.has(p.pid)}
                  onToggle={() => toggle(p.pid)}
                  title={`${p.vp} — ${p.clientName}`}
                  subtitle={`${p.clientNo} · ${p.tier}`}
                  trashedBy={p.trashedBy}
                  trashedAt={p.trashedAt}
                  busy={busy}
                  canPurge={mayPurge}
                  onRestore={() => restoreOne('project', p.pid)}
                  onPurge={() => purgeOne('project', p.pid)}
                />
              ))}
            {tab === 'partners' &&
              partners.map((p) => (
                <TrashRow
                  key={p.pid}
                  id={p.pid}
                  selected={selected.has(p.pid)}
                  onToggle={() => toggle(p.pid)}
                  title={p.name || p.firmName || p.partnerNo}
                  subtitle={`${p.partnerNo}${p.firmName ? ` · ${p.firmName}` : ''}`}
                  trashedBy={p.trashedBy}
                  trashedAt={p.trashedAt}
                  busy={busy}
                  canPurge={mayPurge}
                  onRestore={() => restoreOne('partner', p.pid)}
                  onPurge={() => purgeOne('partner', p.pid)}
                />
              ))}
          </div>
        )}
      </Box>
      )}

      <div>
        <Link to="/" style={{ fontSize: 12.5 }}>
          ‹ Back to Home
        </Link>
      </div>
    </div>
  );
}

/* ════════════════════ AD-038 — property Trash section ════════════════════ */

function PropertyTrashSection({
  properties,
  children,
  busy,
  mayPurge,
  onRestoreProperty,
  onRestoreChild,
  onPurgeChild,
}: {
  properties: LiveProperty[];
  children: DeletedPropertyChild[];
  busy: boolean;
  mayPurge: boolean;
  onRestoreProperty: (propertyId: string) => void;
  onRestoreChild: (child: DeletedPropertyChild) => void;
  onPurgeChild: (child: DeletedPropertyChild) => void;
}) {
  const kindLabel: Record<DeletedPropertyChild['kind'], string> = {
    concept: 'Design concept',
    version: 'Version',
    floor: 'Floor',
  };
  return (
    <>
      {/* Whole soft-deleted properties */}
      <Box
        title="Trashed properties"
        note={`${properties.length} whole propert${properties.length === 1 ? 'y' : 'ies'}`}
      >
        {properties.length === 0 ? (
          <EmptyNote title="Nothing here">No whole properties in the Trash.</EmptyNote>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {properties.map((p) => (
              <Card key={p.propertyId} className="row-card" data-trash-id={p.propertyId}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <b style={{ fontSize: 14, fontFamily: 'monospace' }}>{p.propertyId}</b>
                    <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                      {p.address || 'No address'}
                    </div>
                  </div>
                  <Tag kind="warn">in Trash</Tag>
                  {(p.trashedAt || p.trashedBy) && (
                    <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                      trashed{p.trashedAt ? ` ${String(p.trashedAt).slice(0, 10)}` : ''}
                      {p.trashedBy ? ` · by ${p.trashedBy}` : ''}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Btn3 intent="grn" size="sm" onClick={() => onRestoreProperty(p.propertyId)} disabled={busy}>
                      Restore
                    </Btn3>
                    <span style={{ fontSize: 11, color: 'var(--ink-mute)' }} title="Permanent property purge needs the admin purge Cloud Function (deferred — AD-038).">
                      Permanent delete via admin (deferred)
                    </span>
                  </span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Box>

      {/* Soft-deleted nested concepts / versions / floors */}
      <Box
        title="Trashed design items"
        note={`${children.length} concept/version/floor${children.length === 1 ? '' : 's'}`}
      >
        {children.length === 0 ? (
          <EmptyNote title="Nothing here">No deleted concepts, versions or floors.</EmptyNote>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {children.map((c) => {
              const key = `${c.propertyId}:${c.conceptId}:${c.versionId ?? ''}:${c.floorId ?? ''}`;
              return (
                <Card key={key} className="row-card" data-trash-id={key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0 }}>
                      <b style={{ fontSize: 14 }}>{c.label}</b>
                      <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                        <Link to={`/properties/${c.propertyId}`} style={{ fontFamily: 'monospace' }}>{c.propertyId}</Link>
                        {' · '}{kindLabel[c.kind]}
                      </div>
                    </div>
                    <Tag kind="warn">in Trash</Tag>
                    {c.deletedAt && (
                      <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                        trashed {String(c.deletedAt).slice(0, 10)}
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                      <Btn3 intent="grn" size="sm" onClick={() => onRestoreChild(c)} disabled={busy}>
                        Restore
                      </Btn3>
                      {mayPurge && (
                        <Btn3 intent="ghost" size="sm" onClick={() => onPurgeChild(c)} disabled={busy}>
                          Delete permanently
                        </Btn3>
                      )}
                    </span>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </Box>
    </>
  );
}

/* ════════════════════ Group (family / org) Trash section ════════════════════ */

function GroupTrashSection({
  groups,
  busy,
  onRestore,
}: {
  groups: FamilyGroup[];
  busy: boolean;
  onRestore: (id: string) => void;
}) {
  /** Timestamp → YYYY-MM-DD, guarded (serverTimestamp sentinel resolves late). */
  function trashedWhen(g: FamilyGroup): string | null {
    const ts = g.trashedAt as { toDate?: () => Date } | null | undefined;
    if (ts && typeof ts.toDate === 'function') {
      return ts.toDate().toISOString().slice(0, 10);
    }
    return null;
  }
  return (
    <Box
      title="Trashed groups"
      note={`${groups.length} famil${groups.length === 1 ? 'y' : 'ies'} / organisation${groups.length === 1 ? '' : 's'}`}
    >
      {groups.length === 0 ? (
        <EmptyNote title="Nothing here">No groups in the Trash.</EmptyNote>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {groups.map((g) => {
            const when = trashedWhen(g);
            return (
              <Card key={g.id} className="row-card" data-trash-id={g.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <b style={{ fontSize: 14 }}>{g.name}</b>
                    <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                      {g.type === 'organization' ? 'Organisation' : 'Family'} · {g.members.length}{' '}
                      member{g.members.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <Tag kind="warn">in Trash</Tag>
                  {(when || g.trashedBy) && (
                    <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
                      trashed{when ? ` ${when}` : ''}
                      {g.trashedBy ? ` · by ${g.trashedBy}` : ''}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Btn3 intent="grn" size="sm" onClick={() => onRestore(g.id)} disabled={busy}>
                      Restore
                    </Btn3>
                    <span
                      style={{ fontSize: 11, color: 'var(--ink-mute)' }}
                      title="Permanent purge for groups needs the admin purge Cloud Function (the TRASHABLE allowlist is a deferred functions-repo change)."
                    >
                      Permanent delete for groups coming
                    </span>
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </Box>
  );
}

/* ════════════════════ One trashed-record row ════════════════════ */

function TrashRow({
  id,
  selected,
  onToggle,
  title,
  subtitle,
  trashedBy,
  trashedAt,
  busy,
  canPurge,
  onRestore,
  onPurge,
}: {
  id: string;
  selected: boolean;
  onToggle: () => void;
  title: string;
  subtitle: string;
  trashedBy: string | null;
  trashedAt: string | null;
  busy: boolean;
  canPurge: boolean;
  onRestore: () => void;
  onPurge: () => void;
}) {
  const when = trashedAt ? trashedAt.slice(0, 10) : null;
  return (
    <Card className="row-card" data-trash-id={id}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={`Select ${title}`}
        />
        <div style={{ minWidth: 0 }}>
          <b style={{ fontSize: 14 }}>{title}</b>
          <div style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            {subtitle}
          </div>
        </div>
        <Tag kind="warn">in Trash</Tag>
        {(when || trashedBy) && (
          <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>
            trashed{when ? ` ${when}` : ''}
            {trashedBy ? ` · by ${trashedBy}` : ''}
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Btn3 intent="grn" size="sm" onClick={onRestore} disabled={busy}>
            Restore
          </Btn3>
          {canPurge && (
            <Btn3 intent="ghost" size="sm" onClick={onPurge} disabled={busy}>
              Delete permanently
            </Btn3>
          )}
        </span>
      </div>
    </Card>
  );
}
